import {
  Box,
  Circle,
  Icon,
  Text,
  colors,
  dimensions,
  useUnsafeCallback,
} from '@factorialco/deprecated-design-system'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import { useFinancialWorkspace } from 'modules/finance/components/FinancialWorkspace/Providers/Provider'
import { CustomTooltip } from 'modules/finance/components/FinancialWorkspace/components/CustomTooltip'
import { SkeletonValue } from 'modules/finance/components/FinancialWorkspace/components/SkeletonValue'
import { Delta } from 'modules/finance/components/FinancialWorkspace/components/Table/table/Delta'
import { centToFormattedAmount } from 'modules/finance/components/FinancialWorkspace/components/chart/helpers'
import { useData } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/Providers/DataProvider'
import { MissingGrossSalaries } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/components/MissingGrossSalaries'
import { useEmployeeCostHeaders } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/hooks/useHeaders'
import { FROM_CHART_COMPONENT } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/lib/trackConst'

import { ComboChart } from './ComboChart'
import { Tooltip } from './Tooltip/index'
import {
  SerieIds,
  buildComboChartData,
  computeTotal,
  hasNegativeValue,
  highestDataPoint,
  parseItems,
} from './helpers'
import { useLabels } from './hooks/useLabels'
import { useChartVisibility } from './useVisibility'

const EmployeeCostsChart = () => {
  const labels = useLabels()
  const comparisonLabels = useLabels(true)
  const { headers } = useEmployeeCostHeaders()
  const { formattedState: variables, hasComparisonDate } = useFinancialWorkspace()

  const { canSeeAgreements, canSeeEmployeeExpenses, canSeeLaborCosts } = useChartVisibility()
  const canSeeOnlyAgreements = canSeeAgreements && !canSeeEmployeeExpenses && !canSeeLaborCosts

  const { agreementData, expensesData, payrollData } = useData()
  const items = useUnsafeMemo(
    () => [
      ...(agreementData?.data ?? []),
      ...(expensesData?.data ?? []),
      ...(payrollData?.data ?? []),
    ],
    [agreementData, expensesData, payrollData]
  )

  // For testing with DUMMY_DATA
  // const parsedItems = DUMMY_DATA
  const parsedItems = useUnsafeMemo(
    () => ({ ...parseItems(items, comparisonLabels, true), ...parseItems(items, labels) }),
    [items, labels, comparisonLabels]
  )

  const {
    LABOR_COST,
    AGREEMENT_DATA,
    EMPLOYEE_EXPENSES,
    COMPARISON_LABOR_COST,
    COMPARISON_AGREEMENT_DATA,
    COMPARISON_EMPLOYEE_EXPENSES,
  } = parsedItems

  const {
    access: { locale: accessLocale },
    company: { currency },
  } = useCurrentSession()

  const chartData = useUnsafeMemo(() => buildComboChartData(parsedItems), [parsedItems])

  const { formattedStartDate, formattedCompareToStartDate } = variables

  const comboChartSeries = useUnsafeMemo(() => {
    const series = [
      {
        serieId: formattedStartDate,
        lines: [{ id: SerieIds.AgreementData, color: colors.grey800 }],
        bars: [
          { id: SerieIds.LaborCost, color: 'rgba(85,150,246,1)' },
          { id: SerieIds.EmployeeExpenses, color: colors.moodNeutral },
        ],
      },
    ]

    if (hasComparisonDate) {
      series.unshift({
        serieId: formattedCompareToStartDate,
        lines: [{ id: SerieIds.ComparisonAgreementData, color: colors.grey800 }],
        bars: [
          { id: SerieIds.ComparisonLaborCost, color: 'rgba(85,150,246,1)' },
          { id: SerieIds.ComparisonEmployeeExpenses, color: colors.moodNeutral },
        ],
      })
    }

    return series
  }, [hasComparisonDate, formattedStartDate, formattedCompareToStartDate])

  const openHistoryLog = useUnsafeCallback(
    ({ index, id, serieId }) => {
      if (
        formattedStartDate.slice(0, 4) === formattedCompareToStartDate.slice(0, 4) &&
        serieId === formattedStartDate
      ) {
        index -= 1
      }
      const data = chartData.find((d) => d.id === id)
      const header = headers.find(
        ({ chartLabel }) => chartLabel === `${data?.data?.[index]?.x} ${serieId.slice(0, 4)}`
      )
      header?.onClick?.(header?.value)
    },
    [headers, chartData, formattedStartDate, formattedCompareToStartDate]
  )

  const toAmount = useUnsafeCallback(
    (value: number) => {
      const cents = Number(value) || 0
      return centToFormattedAmount(cents, accessLocale, currency)
    },
    [accessLocale, currency]
  )

  const skeleton = (
    <Box paddingTop="s6">
      <SkeletonValue height="s20" />
    </Box>
  )

  const highestYValue = useUnsafeMemo(() => {
    return highestDataPoint(chartData)?.y || 0
  }, [chartData])

  const upTo1K = highestYValue >= 100000
  const upTo10K = highestYValue >= 1000000

  const noData = useUnsafeMemo(
    () => !(chartData || []).find(({ data }) => data.find(({ y }) => !!y)),
    [chartData]
  )

  const categories = useUnsafeMemo(() => {
    const labels: string[] = []
    chartData.forEach(({ data }) => {
      data.forEach(({ x }) => {
        if (!labels.includes(x)) labels.push(x)
      })
    })
    return labels
  }, [chartData])

  const barCategoryGap = useUnsafeMemo(() => {
    if (categories.length <= 2) return '40%'
    if (categories.length <= 3) return '30%'
  }, [categories])

  const formatLeftAxis = useUnsafeCallback(
    (value: number) => {
      if (noData) return ''
      const label = upTo10K
        ? toAmount(Math.abs(value)).formattedAmount
        : `${toAmount(Math.abs(value)).amount}`.split('.')[0]
      return value < 0 ? `-${label}` : label
    },
    [noData, upTo10K, toAmount]
  )

  const formatTick = useUnsafeCallback((label: string, index: number, serieId?: string) => {
    if (serieId) return `${label} ${serieId.slice(0, 4)}`
    return label
  }, [])

  const margin = useUnsafeMemo(() => {
    const leftMarginOffset = hasNegativeValue(chartData) ? 10 : 0
    let leftMargin = 0

    if (noData) {
      leftMargin = -45
    } else if (upTo1K) {
      leftMargin = -5 + leftMarginOffset
    } else {
      leftMargin = -10 + leftMarginOffset
    }

    return { top: 30, right: 20, bottom: 115, left: leftMargin }
  }, [noData, upTo1K, chartData])

  const totals = (
    <Box
      flexWrap="wrap"
      flexDirection="row"
      alignItems="center"
      data-testid="chart-sources"
      style={{ columnGap: dimensions.s120, rowGap: dimensions.s20 }}
    >
      {canSeeAgreements && (
        <Box data-testid="chart-agreement-data">
          <Box flexDirection="row" gap="s8" alignItems="center">
            <Box style={{ backgroundColor: colors.grey800 }} height="s2" width="s12" />
            <Box flexDirection="row" gap="s4" alignItems="center">
              <Text.Normal weight="medium" size="200">
                {i18n.t('finance.financial_workspace.employee_costs.totals.agreement.label')}
              </Text.Normal>
              <CustomTooltip
                label={i18n.t('finance.financial_workspace.employee_costs.totals.agreement.desc')}
              >
                <Icon.Medium icon="info-circle-line" color="grey800" />
              </CustomTooltip>
            </Box>
          </Box>
          <Box paddingLeft="s16">
            {AGREEMENT_DATA ? (
              <Box flexDirection="row" gap="s4" alignItems="center">
                <Text color="grey1000" weight="bold" size="500">
                  {toAmount(computeTotal(AGREEMENT_DATA)).amount}
                </Text>
                {hasComparisonDate && (
                  <Delta
                    mini
                    secondValue={computeTotal(AGREEMENT_DATA) || 0}
                    firstValue={computeTotal(COMPARISON_AGREEMENT_DATA) || 0}
                  />
                )}
              </Box>
            ) : (
              skeleton
            )}
          </Box>
        </Box>
      )}
      {canSeeEmployeeExpenses && (
        <Box data-testid="chart-employee-expenses">
          <Box flexDirection="row" gap="s8" alignItems="center">
            <Circle color={colors.moodNeutral} size={12} />
            <Box flexDirection="row" gap="s4" alignItems="center">
              <Text.Normal weight="medium" size="200">
                {i18n.t('finance.financial_workspace.employee_costs.totals.expenses.label')}
              </Text.Normal>
              <CustomTooltip
                label={i18n.t('finance.financial_workspace.employee_costs.totals.expenses.desc')}
              >
                <Icon.Medium icon="info-circle-line" color="grey800" />
              </CustomTooltip>
            </Box>
          </Box>
          <Box paddingLeft="s16">
            {EMPLOYEE_EXPENSES ? (
              <Box flexDirection="row" gap="s4" alignItems="center">
                <Text color="grey1000" weight="bold" size="500">
                  {toAmount(computeTotal(EMPLOYEE_EXPENSES)).amount}
                </Text>
                {hasComparisonDate && (
                  <Delta
                    mini
                    secondValue={computeTotal(EMPLOYEE_EXPENSES) || 0}
                    firstValue={computeTotal(COMPARISON_EMPLOYEE_EXPENSES) || 0}
                  />
                )}
              </Box>
            ) : (
              skeleton
            )}
          </Box>
        </Box>
      )}
      {canSeeLaborCosts && (
        <Box data-testid="chart-labor-cost">
          <Box flexDirection="row" gap="s8" alignItems="center">
            <Circle color="#5596F6" size={12} />
            <Box flexDirection="row" gap="s4" alignItems="center">
              <Text.Normal weight="medium" size="200">
                {i18n.t('finance.financial_workspace.employee_costs.totals.payroll.label')}
              </Text.Normal>
              <CustomTooltip
                label={i18n.t('finance.financial_workspace.employee_costs.totals.payroll.desc')}
              >
                <Icon.Medium icon="info-circle-line" color="grey800" />
              </CustomTooltip>
            </Box>
          </Box>
          <Box paddingLeft="s16">
            {LABOR_COST ? (
              <Box flexDirection="row" gap="s4" alignItems="center">
                <Text color="grey1000" weight="bold" size="500">
                  {toAmount(computeTotal(LABOR_COST)).amount}
                </Text>
                {hasComparisonDate && (
                  <Delta
                    mini
                    secondValue={computeTotal(LABOR_COST) || 0}
                    firstValue={computeTotal(COMPARISON_LABOR_COST) || 0}
                  />
                )}
              </Box>
            ) : (
              skeleton
            )}
          </Box>
        </Box>
      )}
    </Box>
  )

  return (
    <Box gap="s16">
      {canSeeOnlyAgreements && (
        <MissingGrossSalaries
          type="banner"
          variables={variables}
          fromComponent={FROM_CHART_COMPONENT}
        />
      )}
      <Box
        width="full"
        padding="s16"
        style={{ height: '450px' }}
        borderRadius={{ all: 'abs016' }}
        border={{ all: { color: 'grey400', width: 's1', style: 'solid' } }}
      >
        {totals}
        <ComboChart
          height="110%"
          margin={margin}
          yAxisTickCount={6}
          chartData={chartData}
          formatTick={formatTick}
          onClick={openHistoryLog}
          series={comboChartSeries}
          showTooltipCursor={false}
          barCategoryGap={barCategoryGap}
          yAxisTickFormatter={(value) => `${formatLeftAxis(value)}`}
          tooltipContent={(props) => (
            <Tooltip tooltipProps={props} toAmount={toAmount} items={parsedItems} />
          )}
        />
        <Box
          gap="s20"
          flexDirection="row"
          alignItems="center"
          justifyContent="center"
          style={{ marginTop: '-70px' }}
        >
          {canSeeAgreements && (
            <Box gap="s8" cursor="pointer" flexDirection="row" alignItems="center">
              <Box style={{ backgroundColor: '#515164' }} height="s2" width="s12" />
              <Text weight="medium" size="100">
                {i18n.t('finance.financial_workspace.employee_costs.totals.agreement.label')}
              </Text>
            </Box>
          )}
          {canSeeEmployeeExpenses && (
            <Box gap="s8" cursor="pointer" flexDirection="row" alignItems="center">
              <Circle color="#F5A51C" size={12} />
              <Text weight="medium" size="100">
                {i18n.t('finance.financial_workspace.employee_costs.totals.expenses.label')}
              </Text>
            </Box>
          )}
          {canSeeLaborCosts && (
            <Box gap="s8" cursor="pointer" flexDirection="row" alignItems="center">
              <Circle color="#5596F6" size={12} />
              <Text weight="medium" size="100">
                {i18n.t('finance.financial_workspace.employee_costs.totals.payroll.label')}
              </Text>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export { EmployeeCostsChart }
